'use client'

import { createContext, useContext, useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import * as Sentry from '@sentry/nextjs'
import { switchProject as switchProjectApi } from '@/lib/api/projects'
import type { Project, ProjectRole, OrgRole } from '@/types/database'

export type ProjectSummary = Pick<Project, 'id' | 'name' | 'domain' | 'role' | 'orgRole'> & {
  /**
   * The engines the project scans. Optional: a summary built since the layout last rendered (a
   * just-tracked brand) lacks it, and `projectAssistantEngines` then gives `BASE_ENGINES`.
   */
  selected_engines?: Project['selected_engines'] | null
}

/** A failed switch reaches Sentry (REL-26) — ids only, no PII — and the console; the caller gets `false`. */
export function reportProjectSwitchFailure(error: unknown, projectId: string): void {
  console.error('Failed to switch project:', error)
  Sentry.captureException(error, { tags: { area: 'project-switch' }, extra: { projectId } })
}

type ProjectContextType = {
  currentProject: ProjectSummary
  projects: ProjectSummary[]
  /** Resolves `true` once the active project is `projectId`, `false` when the switch failed. */
  switchProject: (projectId: string) => Promise<boolean>
  addProject: (project: ProjectSummary) => void
  isSwitching: boolean
  canCreateProject: boolean
  projectLimit: number
  projectCount: number
  /** The current user's role on the active project. */
  currentRole: ProjectRole
  /**
   * The current user's raw org-wide role for the active project's
   * organization. `undefined` only for a `ProjectSummary` that predates
   * growth-engine-r1d T5 (a stale client cache entry, a test fixture) — every
   * server-constructed `Project` sets a concrete value. `useIsClient()`
   * (`src/hooks/use-role.ts`) is the sole reason this is exposed separately
   * from `currentRole`: `client` collapses into the same `currentRole ===
   * 'viewer'` as `analyst`, so only the raw value can distinguish them.
   */
  orgRole: OrgRole | undefined
  /** True when role is 'owner' or 'editor'. */
  canEdit: boolean
  /** True when role is 'owner'. */
  isOwner: boolean
  /** True when role is 'viewer'. */
  isViewer: boolean
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

type ProjectProviderProps = {
  children: React.ReactNode
  initialProject: ProjectSummary
  initialProjects: ProjectSummary[]
  projectLimit: number
}

export function ProjectProvider({
  children,
  initialProject,
  initialProjects,
  projectLimit,
}: ProjectProviderProps) {
  const router = useRouter()
  const [currentProject, setCurrentProject] = useState(initialProject)
  const [projects, setProjects] = useState(initialProjects)
  const [isSwitching, setIsSwitching] = useState(false)

  const switchProject = useCallback(
    async (projectId: string) => {
      if (projectId === currentProject.id) return true

      setIsSwitching(true)

      try {
        const result = await switchProjectApi(projectId)

        if (!result.success) {
          throw new Error(result.error ?? 'Failed to switch project')
        }

        // Optimistically update local state
        const newProject = projects.find((p) => p.id === projectId)
        if (newProject) {
          setCurrentProject(newProject)
        }

        // Refresh server components to pick up new cookie
        router.refresh()
        return true
      } catch (error) {
        reportProjectSwitchFailure(error, projectId)
        return false
      } finally {
        setIsSwitching(false)
      }
    },
    [currentProject.id, projects, router]
  )

  const addProject = useCallback((project: ProjectSummary) => {
    setProjects((prev) => [...prev, project])
    setCurrentProject(project)
  }, [])

  const canCreateProject = projects.length < projectLimit
  const projectCount = projects.length

  const currentRole: ProjectRole = currentProject.role
  const orgRole = currentProject.orgRole
  const canEdit = currentRole === 'owner' || currentRole === 'editor'
  const isOwner = currentRole === 'owner'
  const isViewer = currentRole === 'viewer'

  return (
    <ProjectContext
      value={{
        currentProject,
        projects,
        switchProject,
        addProject,
        isSwitching,
        canCreateProject,
        projectLimit,
        projectCount,
        currentRole,
        orgRole,
        canEdit,
        isOwner,
        isViewer,
      }}
    >
      {children}
    </ProjectContext>
  )
}

export function useProject(): ProjectContextType {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}

/**
 * Non-throwing counterpart to `useProject()` — returns `undefined` instead of
 * throwing when rendered outside a `ProjectProvider`.
 *
 * `useProject()`'s throw is correct for ordinary dashboard components: every
 * mount under `(main)/app/**` has the provider (`gsc-connect-prompt.tsx`
 * documents the one onboarding exception, which supplies its own). This hook
 * exists for the opposite case — a shared component, like
 * `SectionErrorBoundary`, that must render safely both inside `(main)/app/**`
 * and in a context with no provider at all (the `/design-system` gallery has
 * no `ProjectProvider` in its layout). A component reading project context
 * for attribution only, where a missing provider should degrade rather than
 * crash, should use this instead of wrapping `useProject()` in a try/catch.
 */
export function useProjectOptional(): ProjectContextType | undefined {
  return useContext(ProjectContext)
}
